import { useState } from "react";
import type { Question } from "@/domain/entities";
import { Button } from "../Button";
import { useLocale } from "../../i18n/LocaleProvider";
import { SubmitRow } from "./SubmitRow";
import styles from "./questions.module.css";

type MultiQuestion = Extract<Question, { type: "multi" }>;

interface Props {
  question: MultiQuestion;
  value: string[] | undefined;
  optional: boolean;
  onSubmit: (value: string[] | undefined) => void;
}

const OTHER_PREFIX = "other:";

export function MultiInput({ question, value, optional, onSubmit }: Props) {
  const { t, pick } = useLocale();
  const initialOther = value?.find((v) => v.startsWith(OTHER_PREFIX));
  const [picked, setPicked] = useState<Set<string>>(new Set((value ?? []).filter((v) => !v.startsWith(OTHER_PREFIX))));
  const [otherOn, setOtherOn] = useState(initialOther !== undefined);
  const [otherText, setOtherText] = useState(initialOther?.slice(OTHER_PREFIX.length) ?? "");

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    const ids = question.options.filter((o) => picked.has(o.id)).map((o) => o.id);
    const other = otherOn && otherText.trim() ? [`${OTHER_PREFIX}${otherText.trim()}`] : [];
    onSubmit([...ids, ...other]);
  };

  return (
    <div className={styles.stack}>
      <div className={styles.row}>
        {question.options.map((option) => (
          <Button key={option.id} selected={picked.has(option.id)} onClick={() => toggle(option.id)}>
            {pick(option.text)}
          </Button>
        ))}
        {question.allowOther && (
          <Button selected={otherOn} onClick={() => setOtherOn((on) => !on)}>{t.common.other}</Button>
        )}
      </div>
      {otherOn && (
        <input
          className={styles.field}
          placeholder={t.questionnaire.otherPlaceholder}
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
        />
      )}
      <SubmitRow onNext={submit} onSkip={optional ? () => onSubmit(undefined) : undefined} />
    </div>
  );
}
